const GQL_TYPENAME_TO_CONTENT_TYPE = {
  WwwTables: 'wwwTables',
  WwwGallery: 'wwwGallery',
  WwwIFrames: 'wwwIFrames',
  WwwFloatedImageText: 'wwwFloatedImageText',
  WwwDebtConsolidationCards: 'wwwDebtConsolidationCards',
  VideoItem: 'videoItem',
  WwwCalloutBox: 'wwwCalloutBox',
}

/** Guard against malformed or deeply nested rich text documents. */
const MAX_RICH_TEXT_NODE_DEPTH = 500

function gqlAssetToCda(asset) {
  if (!asset?.sys?.id || !asset?.url) {
    return null
  }

  const url = asset.url.startsWith('http') ? asset.url : `https:${asset.url}`

  return {
    sys: { id: asset.sys.id, type: 'Asset' },
    fields: {
      title: asset.title ?? '',
      description: asset.description ?? '',
      file: {
        url: url.replace(/^https:/, ''),
        contentType: asset.contentType ?? '',
        details: {
          image: {
            width: asset.width ?? null,
            height: asset.height ?? null,
          },
        },
      },
    },
  }
}

function resolveOptionalRichText(field) {
  return field?.json ? resolveRichTextFromGql(field) : null
}

function mapEntryFields(entry) {
  const { __typename, sys, ...rest } = entry

  switch (__typename) {
    case 'WwwTables':
      return {
        title: rest.name ?? '',
        variant: rest.tableVariant ?? 'Plain',
        content: rest.content ?? '',
        caption: rest.caption?.json ? resolveRichTextFromGql(rest.caption) : {},
      }
    case 'WwwGallery':
      return {
        images: (rest.imagesCollection?.items ?? [])
          .filter(Boolean)
          .map(gqlAssetToCda),
      }
    case 'WwwIFrames':
      return {
        content: rest.iframeHtml ?? '',
        name: rest.name ?? '',
        variant: rest.variant ?? '',
      }
    case 'WwwFloatedImageText':
      return {
        content: rest.floatedContent
          ? resolveRichTextFromGql(rest.floatedContent)
          : null,
        imageDirection: rest.imageDirection ?? null,
        imageLink: rest.imageLink ?? null,
        image: gqlAssetToCda(rest.image),
      }
    case 'WwwDebtConsolidationCards':
      return {
        title: rest.title ?? '',
        buttonLink: rest.buttonLink ?? '',
        buttonText: rest.buttonText ?? '',
        options: rest.options ?? '',
        image: gqlAssetToCda(rest.image),
      }
    case 'VideoItem':
      return {
        name: rest.name ?? '',
        url: rest.url ?? '',
        aspectRatio: rest.aspectRatio ?? null,
        autoplay: rest.autoplay ?? false,
        description: rest.description ?? '',
        thumbnailImage: gqlAssetToCda(rest.thumbnailImage),
      }
    case 'WwwCalloutBox':
      return {
        name: rest.name ?? '',
        type: rest.type ?? 'callout',
        title: resolveOptionalRichText(rest.calloutTitle || rest.title),
        body: resolveOptionalRichText(rest.calloutBody || rest.body),
        link: rest.link ?? '',
        linkText: rest.linkText ?? '',
      }
    default:
      return Object.entries(rest).reduce((acc, [key, value]) => {
        if (value?.json) {
          acc[key] = resolveRichTextFromGql(value)
        } else if (value?.sys?.id && value?.url) {
          acc[key] = gqlAssetToCda(value)
        } else {
          acc[key] = value
        }

        return acc
      }, {})
  }
}

function gqlEntryToCda(entry) {
  if (!entry?.sys?.id || !entry.__typename) {
    return null
  }

  const contentTypeId = GQL_TYPENAME_TO_CONTENT_TYPE[entry.__typename]
  if (!contentTypeId) {
    return null
  }

  return {
    sys: {
      id: entry.sys.id,
      type: 'Entry',
      contentType: {
        sys: { id: contentTypeId, type: 'Link', linkType: 'ContentType' },
      },
    },
    fields: mapEntryFields(entry),
  }
}

function buildEntryMap(links) {
  const map = new Map()

  for (const entry of links?.entries?.block ?? []) {
    if (!entry?.sys?.id) {
      continue
    }

    const resolved = gqlEntryToCda(entry)
    if (resolved) {
      map.set(entry.sys.id, resolved)
    }
  }

  return map
}

function buildAssetMap(links) {
  const map = new Map()

  for (const asset of links?.assets?.block ?? []) {
    if (!asset?.sys?.id) {
      continue
    }

    const resolved = gqlAssetToCda(asset)
    if (resolved) {
      map.set(asset.sys.id, resolved)
    }
  }

  return map
}

function resolveNode(node, entryMap, assetMap, depth = 0) {
  if (!node || depth > MAX_RICH_TEXT_NODE_DEPTH) {
    return node
  }

  let resolved = node

  if (node.nodeType === 'embedded-entry-block' && node.data?.target?.sys?.id) {
    const target = entryMap.get(node.data.target.sys.id)
    if (target) {
      resolved = {
        ...node,
        data: { ...node.data, target },
      }
    }
  } else if (
    node.nodeType === 'embedded-asset-block' &&
    node.data?.target?.sys?.id
  ) {
    const target = assetMap.get(node.data.target.sys.id)
    if (target) {
      resolved = {
        ...node,
        data: { ...node.data, target },
      }
    }
  }

  if (node.content?.length) {
    resolved = {
      ...resolved,
      content: node.content.map((child) =>
        resolveNode(child, entryMap, assetMap, depth + 1)
      ),
    }
  }

  return resolved
}

/** Merge GraphQL rich text `json` + `links` into CDA-shaped document for RichText renderer. */
export function resolveRichTextFromGql(richText) {
  if (!richText) {
    return null
  }

  if (!richText.json) {
    return richText
  }

  const entryMap = buildEntryMap(richText.links)
  const assetMap = buildAssetMap(richText.links)

  return resolveNode(richText.json, entryMap, assetMap)
}
